import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { DogIcon as Dog, ShareIcon as Share, CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, PencilSimpleIcon as PencilSimple } from "@phosphor-icons/react";
import { thumbUrl } from "@/lib/utils-app";
import { toast } from "sonner";
import BreederBioEditor from "@/components/BreederBioEditor";
import MessageUserButton from "@/components/MessageUserButton";

function AdminTraceBar({ breederId, onLinked }) {
  const [t, setT] = React.useState(null);
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => {
    api.get(`/breeders/${breederId}/admin-trace`).then((r) => setT(r.data)).catch(() => {});
  }, [breederId]);
  React.useEffect(() => { load(); }, [load]);
  if (!t) return null;
  const who = t.created_by_user;
  const linkOwner = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/kennel-verification/${breederId}/link-owner`, { email: email.trim() });
      toast.success(`Kennel linked to ${data.linked_user_email}`);
      setEmail("");
      load();
      onLinked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link owner");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 rounded-lg bg-[#0E0E0E] text-white px-3 py-2.5 text-[11px] leading-relaxed inline-block max-w-full" data-testid="breeder-admin-trace">
      <span className="font-bold uppercase tracking-wider text-[#C9A227] mr-2">Admin only</span>
      Created {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
      {who ? <> by <strong>{who.name}</strong> ({who.email})</> : " — creator account not found"}
      {t.contact_email && <> · kennel contact: {t.contact_email}</>}
      {t.contact_phone && <> · {t.contact_phone}</>}
      <div className="mt-1">
        {t.linked_user ? (
          <span data-testid="trace-linked-account">
            Linked member account: <strong>{t.linked_user.name}</strong> ({t.linked_user.email}) — they can edit the page, upload the logo, and see it as theirs.
          </span>
        ) : (
          <span className="text-[#F0C24B] font-bold" data-testid="trace-not-linked">
            NOT LINKED to any member account — the owner can't edit this page or upload a logo until it's linked.
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && linkOwner()}
          placeholder="member email to link…"
          className="h-7 rounded-full bg-white/10 border border-white/25 px-3 text-[11px] text-white placeholder:text-white/40 w-56 outline-none focus:border-[#C9A227]"
          data-testid="link-owner-email-input"
        />
        <button
          type="button"
          onClick={linkOwner}
          disabled={busy || !email.trim()}
          className="h-7 rounded-full bg-white text-ink font-bold uppercase tracking-wider text-[10px] px-3 hover:bg-[#F4E5B0] transition-colors disabled:opacity-50"
          data-testid="link-owner-btn"
        >
          {busy ? "Linking…" : t.linked_user ? "Change owner" : "Link owner"}
        </button>
        <Link
          to={`/breeders?verify=${breederId}`}
          className="inline-flex items-center gap-1 bg-gold-gradient text-ink font-bold uppercase tracking-wider text-[10px] rounded-full px-2.5 py-1 hover:opacity-90 transition-opacity"
          data-testid="admin-award-seal-btn"
        >
          Award / manage seal
        </Link>
      </div>
    </div>
  );
}
import AdminTools from "@/components/AdminTools";
import VerificationProgressBadge from "@/components/VerificationProgressBadge";
import EliteKennelEditor from "@/components/EliteKennelEditor";
import KennelPhotoHeader from "@/components/KennelPhotoHeader";
import KennelQuickActions from "@/components/KennelQuickActions";
import KennelBenefitsChecklist from "@/components/KennelBenefitsChecklist";
import { WaxSeal } from "@/components/luxury";
import { useAuth } from "@/contexts/AuthContext";
import { isNativeApp, webOrigin } from "@/lib/nativeBridge";
import { recordKennelVisit } from "@/lib/referral-attribution";

export default function BreederDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [breeder, setBreeder] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [myDogs, setMyDogs] = useState([]);
  const [linking, setLinking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Kennel dogs come from the public endpoint (queries by breeder_id
  // server-side) so every viewer — not just the owner — sees the
  // kennel's linked dogs.
  const loadKennelDogs = () =>
    api.get(`/public/breeders/${id}/dogs`).then((r) => setDogs(Array.isArray(r.data) ? r.data : [])).catch(() => {});

  useEffect(() => {
    api
      .get(`/breeders/${id}`)
      .then((r) => setBreeder(r.data))
      .catch(() => setNotFound(true));
    loadKennelDogs();
    // The caller's own dogs — used to offer the owner a one-click
    // "link my dogs" action when some of their dogs aren't attached
    // to any kennel yet (the #1 reason a kennel page looks empty).
    api.get("/dogs").then((r) => setMyDogs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // Last-touch kennel-referral attribution — silent localStorage write
    // + best-effort server analytics ping. The client checkout flow
    // reads this back and passes it as `referrer_kennel_id` when the
    // visitor subscribes to Buyer Pass.
    recordKennelVisit(id);
  }, [id]);

  // Fire-and-forget kennel-view ping for the Breeder Analytics
  // dashboard. Idempotent per (viewer, slug, day); owners viewing
  // their own kennel page are not credited server-side.
  useEffect(() => {
    const slug = breeder?.slug;
    if (!slug) return;
    api.post(`/public/analytics/kennels/${slug}/view-ping`).catch(() => {});
  }, [breeder?.slug]);

  const publicUrl = `${webOrigin()}/k/${id}`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: breeder?.kennel_name || breeder?.name || "Breeder",
          url: publicUrl,
        });
        return;
      }
    } catch {
      /* fall through */
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Public link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (notFound)
    return (
      <AppShell>
        <div className="max-w-md mx-auto text-center py-16" data-testid="breeder-not-found">
          <h2 className="font-serif text-2xl text-ink mb-2">Kennel not found</h2>
          <p className="text-sm text-[#6B6B6B] mb-6">
            This kennel page doesn't exist or may have been removed.
          </p>
          <Link to="/breeders?create=1" className="inline-block rounded-full bg-ink text-white text-sm font-semibold px-6 py-2.5 hover:bg-crimson transition-colors" data-testid="breeder-not-found-create">
            Create your kennel page →
          </Link>
        </div>
      </AppShell>
    );

  if (!breeder)
    return (
      <AppShell>
        <div className="text-[#6B6B6B]">Loading…</div>
      </AppShell>
    );

  // Owner-detection: an owner can change photo, open the quick-action
  // strip, and see the benefits checklist. Anyone else lands on the
  // read-only header. Admins are treated as owners so they can curate
  // a kennel's setup directly.
  const ownerId = breeder.user_id || breeder.owner_id || breeder.created_by;
  const isOwner = !!user && (user.id === ownerId || user.role === "admin");

  // The owner's dogs that aren't attached to ANY kennel yet — offered
  // for one-click linking. Dogs linked to a different kennel are left
  // alone (co-ownership / bought-in dogs).
  const unlinkedDogs = myDogs.filter((d) => !d.breeder_id && !d.pedigree_only);

  const linkMyDogs = async () => {
    setLinking(true);
    try {
      const { data } = await api.post(`/breeders/${breeder.id}/link-my-dogs`);
      toast.success(
        data.linked > 0
          ? `${data.linked} ${data.linked === 1 ? "dog" : "dogs"} linked to your kennel`
          : "No unlinked dogs found"
      );
      loadKennelDogs();
      api.get("/dogs").then((r) => setMyDogs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link dogs");
    } finally {
      setLinking(false);
    }
  };

  return (
    <AppShell>
      <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 mb-6 shadow-luxury-light">
        <div className="flex items-start gap-5">
          <KennelPhotoHeader
            breeder={breeder}
            editable={isOwner}
            onSaved={(patch) => setBreeder({ ...breeder, ...patch })}
          />
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">Kennel</p>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-spectral font-light text-4xl md:text-5xl tracking-tight leading-[1.05]">{breeder.kennel_name || breeder.name}</h1>
              {(breeder.verified || breeder.is_verified || breeder.elite_verified) && (
                <WaxSeal size={42} title="Verified kennel" />
              )}
            </div>
            {breeder.kennel_name && <p className="text-[#6B6B6B] mt-1">{breeder.name}</p>}
            {user?.role === "admin" && (
              <AdminTraceBar
                breederId={id}
                onLinked={() => api.get(`/breeders/${id}`).then((r) => setBreeder(r.data)).catch(() => {})}
              />
            )}
            {breeder && !breeder.user_id && user && user.role !== "admin" && (
              <div className="mt-3 inline-flex items-center gap-3 flex-wrap rounded-lg border border-gold/40 bg-[#FBF7EC] px-3 py-2" data-testid="self-link-banner">
                <span className="text-xs text-[#6B6B6B]">
                  This kennel isn't linked to a member account yet. Is it yours?
                </span>
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/breeders/${id}/claim`, {});
                      if (data.status === "claimed") {
                        toast.success("Kennel linked to your account!");
                        api.get(`/breeders/${id}`).then((r) => setBreeder(r.data)).catch(() => {});
                      } else {
                        toast.success("Request sent — our team will verify and email you.");
                      }
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not send request");
                    }
                  }}
                  className="rounded-full bg-ink hover:bg-crimson text-white text-xs h-8"
                  data-testid="self-link-kennel-btn"
                >
                  Link it to my account
                </Button>
              </div>
            )}
            <div className="mt-3">
              <VerificationProgressBadge breederId={id} />
              {isOwner && !breeder.verified && !isNativeApp() && (
                <Link
                  to="/verified-seal"
                  className="text-xs text-[#8A6A00] underline hover:text-crimson"
                  data-testid="why-get-verified-link"
                >
                  Why get verified?
                </Link>
              )}
            </div>
            <div className="gold-hairline mt-4 mb-4 max-w-md" />
            <div className="space-y-1 text-sm text-[#6B6B6B] font-tabular">
              {breeder.location && <div className="font-sans">{breeder.location}</div>}
              {breeder.contact && <div>{breeder.contact}</div>}
            </div>
            {breeder.bio ? (
              <p className="mt-4 text-ink leading-relaxed">{breeder.bio}</p>
            ) : (
              <p className="mt-4 text-[#6B6B6B] italic text-sm">
                {isOwner
                  ? "No bio yet — add one to help buyers get to know your kennel."
                  : "No bio yet."}
              </p>
            )}
            {!isOwner && (
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <MessageUserButton userId={ownerId} />
                <Button
                  onClick={share}
                  className="rounded-full bg-ink hover:bg-crimson text-white min-h-[44px]"
                  size="sm"
                  data-testid="breeder-share-btn"
                >
                  {copied ? (
                    <>
                      <CheckCircle size={14} className="mr-1" weight="fill" /> Copied
                    </>
                  ) : (
                    <>
                      <Share size={14} className="mr-1" /> Share public profile
                    </>
                  )}
                </Button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#6B6B6B] hover:text-ink px-3 py-1 rounded-full border border-[#EAEAEA] hover:border-gold/55 transition-colors"
                  data-testid="breeder-public-preview-link"
                >
                  Preview <ArrowSquareOut size={12} />
                </a>
              </div>
            )}
            {isOwner && (
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  onClick={() => setBioOpen(true)}
                  size="sm"
                  variant="outline"
                  className="rounded-full border-gold/60 text-[#8A6A00] hover:bg-cream min-h-[44px]"
                  data-testid="breeder-edit-bio-btn"
                >
                  <PencilSimple size={13} className="mr-1" weight="bold" />
                  Edit bio
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isOwner && (
        <>
          <KennelQuickActions
            breeder={breeder}
            publicUrl={publicUrl}
            onShare={share}
          />
          <KennelBenefitsChecklist breeder={breeder} dogCount={dogs.length} />
        </>
      )}

      <BreederBioEditor
        breeder={breeder}
        open={bioOpen}
        onOpenChange={setBioOpen}
        onSaved={(b) => setBreeder({ ...breeder, ...b })}
      />

      {/* Elite-only curation surfaces — owner only; auto-hides for non-Elite. */}
      {isOwner && (
        <EliteKennelEditor
          breeder={breeder}
          onSaved={(patch) => setBreeder({ ...breeder, ...patch })}
        />
      )}

      {/* Verified-badge embed kit. Renders only for kennels that
          actually hold the seal — anyone else's call to the badge
          endpoint 403s, so showing the kit would be misleading. */}
      {breeder.verified && isOwner && <VerifiedBadgeKit breederId={id} />}

      {isOwner && <SlugClaimCard breeder={breeder} onUpdate={setBreeder} />}

      <h2 className="font-spectral text-2xl md:text-3xl tracking-tight mb-1">Dogs from this kennel</h2>
      <div className="gold-hairline mb-5 max-w-md" />
      {isOwner && unlinkedDogs.length > 0 && (
        <div
          className="mb-5 rounded-xl border border-gold/40 bg-gold/5 p-4 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row"
          data-testid="link-my-dogs-banner"
        >
          <div className="text-sm leading-relaxed">
            <p className="font-semibold text-ink">
              {unlinkedDogs.length} of your registered {unlinkedDogs.length === 1 ? "dog isn't" : "dogs aren't"} linked to this kennel yet
            </p>
            <p className="text-[#6B6B6B] mt-0.5">
              Link them so they show up here and on your public kennel page.
            </p>
          </div>
          <Button
            onClick={linkMyDogs}
            disabled={linking}
            className="rounded-full bg-ink hover:bg-crimson text-white flex-shrink-0"
            data-testid="link-my-dogs-btn"
          >
            {linking ? "Linking…" : `Link ${unlinkedDogs.length === 1 ? "my dog" : `all ${unlinkedDogs.length}`}`}
          </Button>
        </div>
      )}
      {dogs.length === 0 ? (
        <div className="text-[#6B6B6B]" data-testid="kennel-no-dogs">No dogs linked yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {dogs.map((d) => (
            <Link
              key={d.id}
              to={`/dogs/${d.id}`}
              className="bg-white border border-[#EAEAEA] hover:border-gold/55 rounded-xl p-4 flex items-center gap-3 shadow-luxury-light hover:shadow-gold-glow hover:-translate-y-0.5 transition-all duration-500"
            >
              <div className="h-14 w-14 rounded-lg overflow-hidden bg-cream flex-shrink-0">
                {d.photos?.[0] ? (
                  <img src={thumbUrl(d.photos[0])} alt="" className="w-full h-full object-cover photo-luxury" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Dog size={20} className="text-gold/40" />
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{d.name}</div>
                <div className="text-xs text-[#6B6B6B] truncate">{d.breed}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
      {breeder && (
        <AdminTools
          recordType="breeder"
          recordId={breeder.id}
          recordName={breeder.kennel_name || breeder.name || breeder.id}
          onDeleted={() => navigate("/breeders")}
        />
      )}
    </AppShell>
  );
}

function SlugClaimCard({ breeder, onUpdate }) {
  const [slug, setSlug] = React.useState(breeder?.slug || "");
  const [busy, setBusy] = React.useState(false);
  if (!breeder) return null;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/breeders/${breeder.id}/slug`, { slug });
      onUpdate({ ...breeder, slug: r.data.slug });
      // sonner already imported at top? if not, fall back to alert.
      if (window?.Notification) {/* noop */}
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not save slug");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 mb-6 max-w-2xl" data-testid="breeder-slug-card">
      <div className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-1">Custom URL</div>
      <p className="text-sm text-[#6B6B6B] mb-3">
        Pick a memorable URL slug — it makes your kennel page easier to share and rank in search.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[#6B6B6B] font-mono">/k/</span>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
          placeholder="your-kennel-name"
          maxLength={60}
          className="flex-1 min-w-[200px] px-3 py-1.5 border border-[#E5E5E5] rounded-md text-sm font-mono"
          data-testid="breeder-slug-input"
        />
        <button
          type="button"
          onClick={save}
          disabled={busy || !slug || slug === (breeder.slug || "")}
          className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-4 py-1.5 text-sm disabled:opacity-50 min-h-[44px]"
          data-testid="breeder-slug-save-btn"
        >
          {busy ? "Saving…" : breeder.slug ? "Update slug" : "Claim slug"}
        </button>
      </div>
      {breeder.slug && (
        <p className="text-xs text-green-700 mt-2">✓ Live at /k/{breeder.slug}</p>
      )}
    </div>
  );
}

/**
 * Verified-badge embed kit. Two PNG variants (gold-on-white,
 * gold-on-black) plus a copy-paste HTML snippet so kennels can drop
 * the WaxSeal on their own website. The badge is generated on every
 * request — if the kennel ever loses the seal, the embedded image
 * 403s and breaks visibly, which is the safety wire.
 */
function VerifiedBadgeKit({ breederId }) {
  const origin = (typeof window !== "undefined" ? webOrigin() : "");
  const lightUrl = `${origin}/api/public/verified-badge/${breederId}/light.png`;
  const darkUrl = `${origin}/api/public/verified-badge/${breederId}/dark.png`;
  const profileUrl = `${origin}/k/${breederId}`;
  const snippet = `<a href="${profileUrl}" target="_blank" rel="noreferrer">\n  <img src="${lightUrl}" alt="Verified by Armstrong K9 Registry" width="160" />\n</a>`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Embed snippet copied");
    } catch {
      toast.error("Could not copy snippet");
    }
  };
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-6 md:p-7 mb-6 shadow-luxury-light" data-testid="verified-badge-kit">
      <div className="flex items-center gap-2 mb-1">
        <WaxSeal size={20} title="Verified" />
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
          Embed kit
        </p>
      </div>
      <h2 className="font-spectral text-2xl tracking-tight">Drop the seal on your website</h2>
      <p className="text-sm text-[#6B6B6B] mt-1 max-w-2xl leading-relaxed">
        Two PNGs, served live from our servers. If your seal is ever revoked, the embed breaks visibly — buyers can trust the badge anywhere they see it.
      </p>
      <div className="gold-hairline mt-4 mb-5 max-w-md" />
      <div className="grid sm:grid-cols-2 gap-5">
        <BadgePreview label="Gold on white" src={lightUrl} dark={false} />
        <BadgePreview label="Gold on black" src={darkUrl} dark={true} />
      </div>
      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">Copy-paste HTML</p>
        <pre className="text-[11px] bg-[#FAFAFA] border border-[#EAEAEA] rounded p-3 font-mono text-[#3a3a3a] overflow-x-auto whitespace-pre">
{snippet}
        </pre>
        <Button onClick={copy} size="sm" className="rounded-full mt-3 min-h-[44px]" data-testid="copy-badge-snippet-btn">
          Copy snippet
        </Button>
      </div>
    </div>
  );
}

function BadgePreview({ label, src, dark }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">{label}</p>
      <div className={`p-5 rounded-lg border ${dark ? "bg-ink border-ink/40" : "bg-cream border-[#E2DAC2]"}`}>
        <img src={src} alt={label} className="block mx-auto rounded" style={{ width: 190, height: "auto" }} />
      </div>
      <a
        href={src}
        download
        className="block text-center mt-2 text-xs underline underline-offset-4 text-ink hover:text-crimson"
        data-testid={`download-badge-${dark ? "dark" : "light"}`}
      >
        Download PNG ↓
      </a>
    </div>
  );
}
